import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, RefreshCw, Clock } from "lucide-react";

export default function ServiceDetail() {
  const { service_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [values, setValues] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/services/${service_id}`).then(({ data }) => {
      setService(data);
      const init = {};
      for (const p of data.required_params || []) init[p.key] = "";
      setValues(init);
    });
  }, [service_id]);

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    setConfirmOpen(true);
  };

  const doSubmit = async (forceRefresh = false) => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/requests", {
        service_id,
        params: values,
        force_refresh: forceRefresh,
      });
      setResult(data);
      setConfirmOpen(false);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) return <div className="text-sm text-text-muted">Caricamento…</div>;

  return (
    <div className="space-y-8 max-w-3xl">
      <button
        onClick={() => navigate("/services")}
        data-testid="back-to-catalog"
        className="text-sm text-text-secondary hover:text-text-primary flex items-center gap-2"
      >
        <ArrowLeft size={16} /> Catalogo servizi
      </button>

      <div>
        <div className="text-[11px] tracking-widest uppercase text-text-muted mb-2">{service.category}</div>
        <h1 className="text-3xl tracking-tight font-medium text-text-primary">{service.name}</h1>
        <p className="text-sm text-text-secondary mt-2 max-w-2xl">{service.description}</p>
      </div>

      <Card className="bg-surface border border-border rounded-xl shadow-sm p-8">
        <form onSubmit={onSubmit} className="space-y-5" data-testid="service-form">
          {(service.required_params || []).map((p) => (
            <div key={p.key} className="space-y-1.5">
              <Label className="text-xs font-medium text-text-secondary">{p.label}</Label>
              <Input
                type={p.type || "text"}
                data-testid={`param-${p.key}`}
                value={values[p.key] || ""}
                onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
                placeholder={p.placeholder}
                required
                className="h-11 rounded-lg border-border focus-visible:ring-accent/30 focus-visible:border-accent"
              />
            </div>
          ))}
          {error && (
            <div data-testid="service-error"
              className="text-sm text-danger bg-danger-subtle border border-danger-subtle px-3 py-2 rounded-lg">{error}</div>
          )}
          <Button
            type="submit" data-testid="service-submit"
            className="rounded-lg bg-accent hover:bg-accent-strong text-white h-11 px-6 font-medium">
            Esegui verifica
          </Button>
        </form>
      </Card>

      {result && (
        <Card className="bg-surface border border-border rounded-xl shadow-sm p-8 animate-fade-in" data-testid="result-card">
          <div className="flex items-start justify-between mb-5 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-text-muted mb-2">Risultato</div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={result.status} cacheHit={result.cache_hit} />
                {result.cache_hit && (
                  <span className="text-xs text-text-secondary flex items-center gap-1.5">
                    <Clock size={12} />
                    Risultato del {new Date(result.cached_from || result.created_at).toLocaleString("it-IT")}
                  </span>
                )}
              </div>
            </div>
            {result.cache_hit && result.status === "completed" && (
              <Button
                variant="outline" size="sm"
                onClick={() => doSubmit(true)}
                disabled={submitting}
                data-testid="refresh-result"
                className="rounded-lg border-border">
                <RefreshCw size={14} className="mr-1.5" />
                Aggiorna
              </Button>
            )}
          </div>

          {result.status === "failed" ? (
            <div className="text-sm text-danger-strong bg-danger-subtle border border-danger-subtle rounded-lg p-4">
              {result.error_message || "Richiesta fallita."}
            </div>
          ) : (
            <pre data-testid="result-json"
              className="bg-subtle border border-border rounded-lg p-4 text-xs text-text-primary overflow-x-auto font-mono leading-relaxed">
              {JSON.stringify(result.response_data, null, 2)}
            </pre>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Conferma richiesta"
        description={`Stai per richiedere "${service.name}". La richiesta verrà registrata e processata.`}
        params={values}
        costEur={user?.role === "admin" && typeof service.cost_cents === "number" ? service.cost_cents / 100 : undefined}
        loading={submitting}
        onConfirm={() => doSubmit(false)}
      />
    </div>
  );
}
